import * as Api from '@ViewModels';
import moment from 'moment';

export const formatDateTime = (value?: string) => (value ? moment(value).format('MMM D, YYYY h:mma') : '');
export const formatDate = (value?: string) => (value ? moment(value).format('MMM D, YYYY') : '');

export const pluralize = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const SOURCE_LABELS: Record<string, string> = {
	[Api.CampaignComplianceSource.WebOptInForm]: 'Web opt-in form',
	[Api.CampaignComplianceSource.PrivacyPolicy]: 'Privacy policy',
	[Api.CampaignComplianceSource.Terms]: 'Terms & conditions',
	[Api.CampaignComplianceSource.Website]: 'Website',
	[Api.CampaignComplianceSource.OptInScript]: 'Verbal opt-in script',
	[Api.CampaignComplianceSource.CampaignDescription]: 'Campaign description',
	[Api.CampaignComplianceSource.Unknown]: 'Other',
};

const SOURCE_ORDER: string[] = [
	Api.CampaignComplianceSource.WebOptInForm,
	Api.CampaignComplianceSource.PrivacyPolicy,
	Api.CampaignComplianceSource.Terms,
	Api.CampaignComplianceSource.OptInScript,
	Api.CampaignComplianceSource.CampaignDescription,
	Api.CampaignComplianceSource.Website,
	Api.CampaignComplianceSource.Unknown,
];

export const getSourceLabel = (source?: string) => {
	if (!source) {
		return SOURCE_LABELS[Api.CampaignComplianceSource.Unknown];
	}
	return SOURCE_LABELS[source] ?? source;
};

const CATEGORY_LABELS: Record<string, string> = {
	[Api.CampaignComplianceCategory.RequiredDisclosure]: 'Required disclosure',
	[Api.CampaignComplianceCategory.ConsentStructure]: 'Consent structure',
	[Api.CampaignComplianceCategory.DocumentAccess]: 'Document access',
	[Api.CampaignComplianceCategory.Unverifiable]: 'Unverifiable',
	[Api.CampaignComplianceCategory.Unknown]: 'Uncategorised',
};

export const getCategoryLabel = (category?: string) => {
	if (!category) {
		return CATEGORY_LABELS[Api.CampaignComplianceCategory.Unknown];
	}
	return CATEGORY_LABELS[category] ?? category;
};

const MODE_LABELS: Record<string, string> = {
	[Api.CampaignComplianceReviewMode.Enforce]: 'Enforcing',
	[Api.CampaignComplianceReviewMode.Shadow]: 'Shadow mode',
	[Api.CampaignComplianceReviewMode.Off]: 'Review off',
};

export const getModeLabel = (mode?: string) => (mode ? (MODE_LABELS[mode] ?? mode) : null);

export type StatusTone = 'green' | 'amber' | 'red' | 'gray';

export const getCampaignStatusTone = (status?: string): StatusTone => {
	switch (status) {
		case Api.CampaignRegistrationStatus.Accepted:
		case Api.CampaignRegistrationStatus.Verified:
			return 'green';
		case Api.CampaignRegistrationStatus.Pending:
			return 'amber';
		case Api.CampaignRegistrationStatus.Declined:
		case Api.CampaignRegistrationStatus.Failed:
			return 'red';
		default:
			return 'gray';
	}
};

export const getBrandStatusTone = (status?: string): StatusTone => {
	switch (status) {
		case Api.TextingBrandStatus.Verified:
			return 'green';
		case Api.TextingBrandStatus.Pending:
			return 'amber';
		case Api.TextingBrandStatus.Failed:
			return 'red';
		default:
			return 'gray';
	}
};

export interface DerivedCompliance {
	review?: Api.CampaignComplianceReview;
	runs: Api.CampaignComplianceRun[];
	overrides: Api.CampaignComplianceOverride[];
	latestRun?: Api.CampaignComplianceRun;
	latestOverride?: Api.CampaignComplianceOverride;
	blockers: Api.CampaignComplianceFinding[];
	warnings: Api.CampaignComplianceFinding[];
	resolved: Api.CampaignComplianceFinding[];
}

export const deriveCompliance = (review?: Api.CampaignComplianceReview): DerivedCompliance => {
	const runs = review?.runs ?? [];
	const findings = review?.findings ?? [];
	const overrides = review?.overrides ?? [];
	const outstanding = findings.filter(finding => !finding.resolvedAt);
	return {
		review,
		runs,
		overrides,
		latestRun: runs.length ? runs[runs.length - 1] : undefined,
		latestOverride: overrides.length ? overrides[overrides.length - 1] : undefined,
		blockers: outstanding.filter(finding => finding.severity === Api.CampaignComplianceSeverity.Blocker),
		warnings: outstanding.filter(finding => finding.severity === Api.CampaignComplianceSeverity.Warning),
		resolved: findings.filter(finding => !!finding.resolvedAt),
	};
};

export const areAllCampaignsAccepted = (campaigns: Api.TextingProviderCampaign[]) =>
	campaigns.length > 0 && campaigns.every(campaign => campaign.status === Api.CampaignRegistrationStatus.Accepted);

// Accounts accumulate historical campaigns per provider; only the live ones tell the current story.
export const filterCampaignsForDisplay = (campaigns: Api.TextingProviderCampaign[]): Api.TextingProviderCampaign[] => {
	const byProvider = new Map<Api.TelephonyServiceProvider, Api.TextingProviderCampaign[]>();
	campaigns.forEach(campaign => {
		byProvider.set(campaign.provider, [...(byProvider.get(campaign.provider) ?? []), campaign]);
	});
	const result: Api.TextingProviderCampaign[] = [];
	byProvider.forEach(providerCampaigns => {
		const acceptedAndPending = providerCampaigns.filter(
			campaign =>
				campaign.status === Api.CampaignRegistrationStatus.Accepted ||
				campaign.status === Api.CampaignRegistrationStatus.Pending
		);
		if (acceptedAndPending.length) {
			result.push(...acceptedAndPending);
			return;
		}
		result.push(
			providerCampaigns.reduce((latest, campaign) => {
				if (!campaign.lastSubmissionDate) {
					return latest;
				}
				if (!latest.lastSubmissionDate || moment(campaign.lastSubmissionDate).isAfter(latest.lastSubmissionDate)) {
					return campaign;
				}
				return latest;
			})
		);
	});
	return result;
};

export interface FindingSourceGroup {
	source: string;
	findings: Api.CampaignComplianceFinding[];
}

export const groupFindingsBySource = (findings: Api.CampaignComplianceFinding[]): FindingSourceGroup[] => {
	const bySource = new Map<string, Api.CampaignComplianceFinding[]>();
	findings.forEach(finding => {
		const source = finding.evidence?.[0]?.source ?? Api.CampaignComplianceSource.Unknown;
		bySource.set(source, [...(bySource.get(source) ?? []), finding]);
	});
	const orderedSources = SOURCE_ORDER.filter(source => bySource.has(source));
	bySource.forEach((_, source) => {
		if (!orderedSources.includes(source)) {
			orderedSources.push(source);
		}
	});
	return orderedSources.map(source => ({ source, findings: bySource.get(source) }));
};

// The API never says whether a release's submission went out, so a campaign submitted at or after the
// release is the only evidence it did. None means the release needs retrying.
// The two timestamps are stamped independently server-side, so tolerate a minute of skew between them.
const RELEASE_SUBMISSION_SKEW_MINUTES = 1;

export const findCampaignSubmittedUnderRelease = (
	campaigns: Api.TextingProviderCampaign[],
	override?: Api.CampaignComplianceOverride
) => {
	if (!override?.at) {
		return undefined;
	}
	const earliestSubmission = moment(override.at).subtract(RELEASE_SUBMISSION_SKEW_MINUTES, 'minutes');
	return campaigns.find(
		campaign => campaign.lastSubmissionDate && !moment(campaign.lastSubmissionDate).isBefore(earliestSubmission)
	);
};

export const buildFixesClipboardText = (blockers: Api.CampaignComplianceFinding[]) =>
	blockers
		.map((blocker, index) => {
			const lines = [`${index + 1}. ${blocker.title ?? blocker.code}`];
			if (blocker.suggestedFix) {
				lines.push(`   ${blocker.suggestedFix}`);
			}
			if (blocker.levitateHelpUrl) {
				lines.push(`   Guidance: ${blocker.levitateHelpUrl}`);
			}
			return lines.join('\n');
		})
		.join('\n\n');

export const VERIFY_LABEL = 'Check the client’s pages again';

export type VerdictTone = 'fail' | 'pass' | 'warn' | 'info' | 'neutral';

export interface ComplianceVerdict {
	kind: 'held' | 'failingAdvisory' | 'passing' | 'released' | 'releaseNotSubmitted' | 'errored' | 'neverRun';
	tone: VerdictTone;
	icon: string;
	headline: string;
	sub: string;
	reason?: string;
	next?: string;
	verify: 'primary' | 'secondary' | 'quiet';
	showRelease: boolean;
	showRetry: boolean;
	runFriction: 'none' | 'passing' | 'overridden';
	collapseFindings: boolean;
	findingsLabel: string;
}

const DEFAULT_FINDINGS_LABEL = 'What the client needs to fix';

export const deriveVerdict = (
	compliance: DerivedCompliance,
	campaigns: Api.TextingProviderCampaign[],
	// Whether a submission went out under a release is a question about history, so it is asked of every
	// campaign: the display filter can drop the very declined campaign that proves one did.
	allCampaigns: Api.TextingProviderCampaign[]
): ComplianceVerdict | null => {
	// Accepted campaigns mean nothing is waiting on anyone, so no banner: the status line carries the story.
	if (areAllCampaignsAccepted(campaigns)) {
		return null;
	}
	const { review, runs, latestRun, latestOverride, blockers } = compliance;
	const blockerCount = pluralize(blockers.length, 'blocker');
	const lastReviewedSub = latestRun
		? `Last reviewed ${formatDateTime(latestRun.ranAt)} · triggered by ${latestRun.trigger}`
		: '';

	if (!latestRun) {
		return {
			kind: 'neverRun',
			tone: 'neutral',
			icon: '○',
			headline: 'No compliance review has been run',
			sub: 'This registration predates the pre-submission review.',
			next: 'Check the client’s opt-in form, privacy policy and website now, rather than finding out from a decline.',
			verify: 'primary',
			showRelease: false,
			showRetry: false,
			runFriction: 'none',
			collapseFindings: false,
			findingsLabel: DEFAULT_FINDINGS_LABEL,
		};
	}

	if (latestRun.errorMessage) {
		const lastCompleted = [...runs].reverse().find(run => !run.errorMessage);
		const findingsOrigin = lastCompleted
			? ` The findings below come from the last review that finished, on ${formatDate(lastCompleted.ranAt)}.`
			: '';
		return {
			kind: 'errored',
			tone: 'info',
			icon: '⚠',
			headline: 'Latest review could not complete',
			sub: `${formatDateTime(latestRun.ranAt)} · “${latestRun.errorMessage}”`,
			next: `Reviews fail open, so nothing was held by this.${findingsOrigin}`,
			verify: 'primary',
			showRelease: false,
			showRetry: false,
			runFriction: 'none',
			collapseFindings: false,
			findingsLabel: DEFAULT_FINDINGS_LABEL,
		};
	}

	if (review?.isLatestRunOverridden && latestOverride) {
		const releasedBy = latestOverride.by ?? 'an admin';
		const reason = latestOverride.reason ? `“${latestOverride.reason}”` : undefined;
		const releasedPrefix = `Released by ${releasedBy} · ${formatDateTime(latestOverride.at)}`;
		const submitted = findCampaignSubmittedUnderRelease(allCampaigns, latestOverride);
		if (submitted) {
			const overriddenRun = runs.find(run => run.id && run.id === latestOverride.runId);
			const covering = overriddenRun ? `the failed ${formatDate(overriddenRun.ranAt)} review` : 'the failed review';
			return {
				kind: 'released',
				tone: 'warn',
				icon: '⚑',
				headline: 'Hold released — campaign submitted',
				sub: `${releasedPrefix} · covering ${covering}`,
				reason,
				next: `Submitted to ${submitted.provider} at ${moment(submitted.lastSubmissionDate).format(
					'h:mma'
				)} under this release. The findings were never fixed, so the provider may still decline it.`,
				verify: 'quiet',
				showRelease: false,
				showRetry: false,
				runFriction: 'overridden',
				collapseFindings: true,
				findingsLabel: 'Findings at time of release',
			};
		}
		return {
			kind: 'releaseNotSubmitted',
			tone: 'fail',
			icon: '⛔',
			headline: 'Hold released — but the submission did not go out',
			sub: `${releasedPrefix} · no campaign reached a provider`,
			reason,
			next: 'The release is on the record. Releasing again retries the submission and does not record a second release.',
			verify: 'quiet',
			showRelease: false,
			showRetry: true,
			runFriction: 'overridden',
			collapseFindings: true,
			findingsLabel: 'Findings at time of release',
		};
	}

	if (review?.passed) {
		return {
			kind: 'passing',
			tone: 'pass',
			icon: '✓',
			headline: 'Compliance review passed',
			sub: lastReviewedSub,
			verify: 'secondary',
			showRelease: false,
			showRetry: false,
			runFriction: 'passing',
			collapseFindings: false,
			findingsLabel: DEFAULT_FINDINGS_LABEL,
		};
	}

	if (review?.mode === Api.CampaignComplianceReviewMode.Enforce) {
		return {
			kind: 'held',
			tone: 'fail',
			icon: '⛔',
			headline: `Registration held — ${blockerCount}`,
			sub: lastReviewedSub,
			next: 'The submission stays held until these are fixed. Send the client the fixes below, then re-check — a review that passes clears the hold.',
			verify: 'primary',
			showRelease: true,
			showRetry: false,
			runFriction: 'none',
			collapseFindings: false,
			findingsLabel: DEFAULT_FINDINGS_LABEL,
		};
	}

	return {
		kind: 'failingAdvisory',
		tone: 'warn',
		icon: '⚠',
		headline: `Review failed — ${blockerCount}`,
		sub: lastReviewedSub,
		next:
			review?.mode === Api.CampaignComplianceReviewMode.Shadow
				? 'Nothing is held: shadow mode never blocks, and the campaign was submitted as normal. These findings would stop it once enforcement is on — and the provider may decline it for them in the meantime.'
				: undefined,
		verify: 'primary',
		showRelease: false,
		showRetry: false,
		runFriction: 'none',
		collapseFindings: false,
		findingsLabel: DEFAULT_FINDINGS_LABEL,
	};
};
